import { Button as MantineButton } from '@mantine/core'
import type React from 'react'
import styles from './Button.module.css'
import { Typography } from './Typography'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type ButtonVariant =
  | 'primary'
  | 'primaryOutlined'
  | 'secondary'
  | 'tertiary'
  | 'system'
  | 'system-back'
  | 'danger'

export type ButtonSize = 'lg' | 'md' | 'sm' | 'xs'

export type ButtonProps = {
  /** ボタンに表示するテキスト。lg/md サイズは button1、sm/xs サイズは button2 タイポグラフィで描画される */
  label: string
  /** ボタンの見た目バリアント（デフォルト: 'primary'） */
  variant?: ButtonVariant
  /** ボタンのサイズ（デフォルト: 'md'） */
  size?: ButtonSize
  /** true にするとピル形状（border-radius 32px）になる */
  round?: boolean
  /** ローディングスピナーを表示する */
  loading?: boolean
  disabled?: boolean
  /** ボタン左側のアイコン */
  leftSection?: React.ReactNode
  /** ボタン右側のアイコン */
  rightSection?: React.ReactNode
  /** 親要素の幅いっぱいに広げる */
  fullWidth?: boolean
  /** Storybook 用: Hover/Pressed 状態を強制表示する */
  forceState?: 'hover' | 'pressed'
  /** 指定すると内部で window.open を呼ぶ */
  href?: string
  target?: '_self' | '_blank'
  type?: 'button' | 'submit' | 'reset'
  onClick?: () => void
  className?: string
  /** @deprecated className を使ってください */
  // classes?: string
  // 以下は旧 API の互換プロップ（無視される）
  /** @deprecated variant を使ってください */
  // bgColor?: string
  // /** @deprecated variant を使ってください */
  // borderColor?: string
  // /** @deprecated size を使ってください */
  // textSize?: string
  // /** @deprecated size を使ってください */
  // paddingY?: string
  // /** @deprecated fullWidth を使ってください */
  // width?: string
}

// ─────────────────────────────────────────────
// バリアントごとのカラー設定
// ─────────────────────────────────────────────
type VariantTokens = {
  bg: string
  hover: string
  pressed: string
  color: string
  /** Mantine --button-bd 形式（例: '1px solid #FFC500' or 'none'） */
  border: string
  disabledBg: string
  disabledColor: string
  disabledBorder: string
  /** Mantine variant: filled or outline */
  mantineVariant: 'filled' | 'outline'
}

const VARIANT_TOKENS: Record<ButtonVariant, VariantTokens> = {
  primary: {
    bg: 'var(--mantine-color-primary-5)',
    hover: 'var(--mantine-color-primary-6)',
    pressed: 'var(--mantine-color-primary-7)',
    color: 'var(--sub-color)',
    border: 'none',
    disabledBg: 'var(--mantine-color-primary-0)',
    disabledColor: 'var(--sub-color)',
    disabledBorder: 'none',
    mantineVariant: 'filled',
  },
  primaryOutlined: {
    bg: '#FFFFFF',
    hover: 'var(--mantine-color-primary-0)',
    pressed: 'var(--mantine-color-primary-1)',
    color: 'var(--mantine-color-primary-5)',
    border: '1px solid var(--mantine-color-primary-5)',
    disabledBg: '#FFFFFF',
    disabledColor: 'var(--sub-color)',
    disabledBorder: '1px solid #CBCBCE',
    mantineVariant: 'outline',
  },
  secondary: {
    bg: 'var(--mantine-color-secondary-5)',
    hover: 'var(--mantine-color-secondary-6)',
    pressed: 'var(--mantine-color-secondary-7)',
    color: '#000000',
    border: 'none',
    disabledBg: 'var(--mantine-color-secondary-0)',
    disabledColor: 'var(--sub-color)',
    disabledBorder: 'none',
    mantineVariant: 'filled',
  },
  tertiary: {
    bg: '#5BBF3A',
    hover: '#4DA532',
    pressed: '#3F8C2A',
    color: '#000000',
    border: 'none',
    disabledBg: '#F2FBF0',
    disabledColor: 'var(--sub-color)',
    disabledBorder: 'none',
    mantineVariant: 'filled',
  },
  system: {
    bg: '#d3d3d3',
    // bg: '#757575',
    hover: '#d3d3d3',
    pressed: '#d3d3d3',
    // pressed: '#424242',
    color: '#FFFFFF',
    border: 'none',
    disabledBg: '#EEEEEE',
    disabledColor: 'var(--sub-color)',
    disabledBorder: 'none',
    mantineVariant: 'filled',
  },
  'system-back': {
    // 戻る系ボタン: 灰色の枠線 / 白背景 / 灰色文字
    bg: '#FFFFFF',
    hover: '#F5F5F5',
    pressed: '#EEEEEE',
    color: '#757575',
    border: '1px solid #757575',
    disabledBg: '#FFFFFF',
    disabledColor: '#CBCBCE',
    disabledBorder: '1px solid #CBCBCE',
    mantineVariant: 'outline',
  },
  danger: {
    bg: '#D46A6A',
    hover: '#C55757',
    pressed: '#AD4343',
    color: '#FFFFFF',
    border: 'none',
    disabledBg: '#FBF1F1',
    disabledColor: 'var(--sub-color)',
    disabledBorder: 'none',
    mantineVariant: 'filled',
  },
}

// ─────────────────────────────────────────────
// サイズ設定（Figma 仕様）
// ─────────────────────────────────────────────
const SIZE_CONFIG: Record<ButtonSize, { height: number; fontSize: number; px: number }> = {
  lg: { height: 56, fontSize: 15, px: 16 },
  md: { height: 48, fontSize: 15, px: 16 },
  sm: { height: 36, fontSize: 13, px: 16 },
  xs: { height: 32, fontSize: 13, px: 16 },
}

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
export const Button = ({
  label,
  // text,
  variant = 'primary',
  size = 'md',
  round = false,
  loading,
  disabled,
  leftSection,
  rightSection,
  fullWidth,
  forceState,
  href,
  target = '_self',
  type = 'button',
  onClick,
  className,
}: ButtonProps) => {
  const vt = VARIANT_TOKENS[variant]
  const sc = SIZE_CONFIG[size]
  const resolvedClassName = className
  const typographyVariant = size === 'lg' || size === 'md' ? 'button1' : 'button2'

  const forceStateClass =
    forceState === 'hover' ? styles.forceHover : forceState === 'pressed' ? styles.forceActive : undefined

  const handleClick = () => {
    if (href) {
      window.open(href, target, 'noopener,noreferrer')
    } else {
      onClick?.()
    }
  }

  return (
    <MantineButton
      variant={vt.mantineVariant}
      type={type}
      disabled={disabled}
      loading={loading}
      loaderProps={{ size: sc.height <= 36 ? 'xs' : 'sm' }}
      leftSection={leftSection}
      rightSection={rightSection}
      fullWidth={fullWidth}
      radius={round ? 'xl' : 'md'}
      onClick={handleClick}
      className={resolvedClassName}
      classNames={{ root: [styles.root, forceStateClass].filter(Boolean).join(' ') }}
      style={
        {
          // Mantine CSS 変数で色・ボーダーを上書き
          '--button-bg': vt.bg,
          '--button-hover': vt.hover,
          '--button-color': vt.color,
          '--button-bd': vt.border,
          // disabled 時の値（Button.module.css で参照）
          '--btn-disabled-bg': vt.disabledBg,
          '--btn-disabled-color': vt.disabledColor,
          '--btn-disabled-border': vt.disabledBorder,
          // pressed 時の値（Button.module.css で参照）
          '--btn-pressed-bg': vt.pressed,
          // サイズ
          height: `${sc.height}px`,
          paddingLeft: `${sc.px}px`,
          paddingRight: `${sc.px}px`,
        } as React.CSSProperties
      }
    >
      <Typography variant={typographyVariant} component="span">
        {label}
      </Typography>
    </MantineButton>
  )
}
